import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useI18n } from "@/providers/i18n-provider";
import { useLearnerStats } from "@/features/courses/hooks/use-course-data";
import { Award, BookOpen, Flame, Trophy } from "lucide-solid";

interface StatsSectionProps {
  userId: string;
}

/**
 * Estadísticas del alumno (Space): XP, lecciones completadas y racha. Reemplaza
 * al panel de stats de tareas del template (contraído con el dominio todo).
 */
export function StatsSection(_props: StatsSectionProps) {
  const { t } = useI18n();
  const stats = useLearnerStats();

  const tiles = () => [
    {
      label: t(msg`XP total`),
      value: stats.data?.total_xp ?? 0,
      icon: Award,
      color: "var(--joy-sun)",
    },
    {
      label: t(msg`Lessons`),
      value: stats.data?.lessons_done ?? 0,
      icon: BookOpen,
      color: "var(--joy-mint)",
    },
    {
      label: t(msg`Streak`),
      value: stats.data?.current_streak ?? 0,
      icon: Flame,
      color: "var(--joy-flame)",
    },
    {
      label: t(msg`Best streak`),
      value: stats.data?.longest_streak ?? 0,
      icon: Trophy,
      color: "var(--joy-grape)",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t(msg`Your progress`)}</CardTitle>
      </CardHeader>
      <CardContent>
        <div class="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="stats-section">
          <Show
            when={!stats.isLoading || stats.data}
            fallback={
              <div class="col-span-full text-sm text-muted-foreground">{t(msg`Loading…`)}</div>
            }
          >
            <For each={tiles()}>
              {(tile) => (
                <div
                  class="flex flex-col items-center gap-1 rounded-2xl border border-border p-4 text-center transition-transform hover:-translate-y-0.5"
                  style={{
                    "background-color": `color-mix(in oklch, ${tile.color} 10%, transparent)`,
                  }}
                >
                  <tile.icon class="h-6 w-6" style={{ color: tile.color }} />
                  <span class="font-display text-2xl font-extrabold">{tile.value}</span>
                  <span class="text-xs font-medium text-muted-foreground">{tile.label}</span>
                </div>
              )}
            </For>
          </Show>
        </div>
      </CardContent>
    </Card>
  );
}
